/**
 * Stripe Payment
 */

import { Box, Button } from '@everlywell/ui-kit';
import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import { PaymentRequestTokenEvent, PaymentRequest } from '@stripe/stripe-js';
import ApplePayWidget from 'components/ApplePayWidget';
import React, { useState, useEffect, useRef } from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { CHECKOUT } from 'utils/constants/dataTest';
import { PRODUCT_SLUGS } from 'utils/constants/products';
import { logError } from 'utils/helpers';

import { VariantsSkusType } from '../../../utils/types';

type StripePaymentRequestButtonType = 'default' | 'book' | 'buy' | 'donate';
type StripePaymentRequestButtonTheme = 'dark' | 'light' | 'light-outline';

export type StripePaymentProps = {
  setTOS?: Function;

  cartTotal: number;

  handlePaymentConfirm: (
    data: Omit<PaymentRequestTokenEvent, 'token' | 'complete'>,
    token: PaymentRequestTokenEvent['token'],
  ) => Promise<void>;
  onStripeLoadingError?: (hasError: boolean) => void;

  optionType?: StripePaymentRequestButtonType;
  optionTheme?: StripePaymentRequestButtonTheme;
  withApplePayWidget?: boolean;
  buttonHeight?: number | null;

  label: string;
  variantsSkus: VariantsSkusType;

  className?: string;
};

export const StripePayment: React.FC<StripePaymentProps> = ({
  cartTotal,
  setTOS,
  optionType = 'default',
  optionTheme = 'dark',
  label,
  handlePaymentConfirm,
  variantsSkus,
  withApplePayWidget = true,
  className,
  onStripeLoadingError: onLoadingError,
  buttonHeight: _buttonHeight,
}: StripePaymentProps) => {
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>();
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [hasStripeLoaded, setHasStripeLoaded] = useState(false);
  const [isStripeError, setIsStripeError] = useState(false);

  const buttonHeight = _buttonHeight ?? 48;
  // variantsSkus holds variant SKUs (e.g. `caris-detect-mced-test-M`), not
  // product SKUs, so they must be matched against the product slug prefix
  // rather than compared for equality.
  const hasCarisProduct = (variantsSkus ?? []).some((sku) =>
    sku.startsWith(PRODUCT_SLUGS.CARIS_DETECT),
  );

  // Keep confirm + analytics payloads current without tearing down the
  // PaymentRequest on every parent re-render / SKU switch.
  const handlePaymentConfirmRef = useRef(handlePaymentConfirm);
  const variantsSkusRef = useRef(variantsSkus);
  handlePaymentConfirmRef.current = handlePaymentConfirm;
  variantsSkusRef.current = variantsSkus;

  // TODO: Refactor this so we only call this if there is an error. Or rename this method to be more clear.
  const onStripeLoadingError = React.useCallback(
    (value: boolean) => {
      onLoadingError?.(value);
    },
    [onLoadingError],
  );

  const stripe = useStripe();

  useEffect(() => {
    paymentRequest &&
      paymentRequest.update({
        total: {
          label: 'Everly Wellness, Inc',
          amount: Number(cartTotal.toFixed(2)),
        },
      });
  }, [paymentRequest, cartTotal]);

  useEffect(() => {
    canMakePayment && setTOS?.(true);
  }, [canMakePayment, setTOS]);

  useEffect(() => {
    if (!stripe) return;

    // hasCarisProduct can flip as line items load in, which re-runs this
    // effect. Guard against a slower, now-stale canMakePayment() call
    // resolving after a newer one and clobbering its up-to-date
    // requestPayerPhone/paymentRequest state.
    let isStale = false;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Everly Wellness, Inc',
        amount: 0,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      ...(hasCarisProduct && { requestPayerPhone: true }),
      requestShipping: true,
      shippingOptions: [
        {
          id: 'free-shipping',
          label: 'Free shipping',
          detail: 'Arrives in 2 to 8 business days',
          amount: 0,
        },
      ],
    });

    pr.canMakePayment()
      .then((result) => {
        if (isStale) return;

        // https://docs.stripe.com/js/payment_request/can_make_payment
        ewAnalytics.track({
          event: ANALYTICS.EVENTS.EXPRESS_PAY_OPTIONS,
          data: { ...result },
        });

        setHasStripeLoaded(true);

        if (result) {
          setCanMakePayment(!!result);
          setPaymentRequest(pr);
        } else {
          throw new Error('Stripe payment request not available');
        }
      })
      .catch((error) => {
        if (isStale) return;

        setIsStripeError(true);
        onStripeLoadingError(true);
        logError(`Error setting canMakePayment method in Stripe: ${error}`, {
          progname: 'order_processing',
          component: 'StripePayment',
          method: 'canMakePayment',
          error,
        });
      });

    return () => {
      isStale = true;
    };
  }, [stripe, onStripeLoadingError, hasCarisProduct]);

  useEffect(() => {
    const tokenHandler = (event: PaymentRequestTokenEvent) => {
      // stripe validates the
      const { token, complete, ...data } = event;

      ewAnalytics.track({
        event: ANALYTICS.EVENTS.EXPRESS_PAY,
        data: {
          label: data.walletName,
          variant_skus: variantsSkusRef.current,
        },
      });

      // execute the payment to our backend
      handlePaymentConfirmRef.current(data, token).then(() => {
        complete('success');
      });
    };

    paymentRequest?.on('token', tokenHandler);

    return () => {
      paymentRequest?.off('token', tokenHandler);
    };
  }, [paymentRequest]);

  useEffect(() => {
    const cancelHandler = function () {
      ewAnalytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: ANALYTICS.LABELS.EXPRESS_PAY_CANCEL,
        },
      });
    };
    paymentRequest?.on('cancel', cancelHandler);

    return () => {
      paymentRequest?.off('cancel', cancelHandler);
    };
  }, [paymentRequest]);

  // https://stripe.com/docs/js/appendix/style
  // https://docs.stripe.com/stripe-js/elements/payment-request-button#html-js-styling-the-element
  const options = {
    style: {
      paymentRequestButton: {
        type: optionType,
        theme: optionTheme,
        height: `${buttonHeight}px`,
        borderRadius: '28px', // New DLS CSS Override
      },
      base: {
        fontsize: '14px',
      },
    },
  };

  const trackClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.EXPRESS_PAY_CHECKOUT,
      },
    });
  };

  if (isStripeError) {
    return null;
  }

  if (hasStripeLoaded) {
    if (canMakePayment && paymentRequest) {
      return (
        <Box
          data-testid={CHECKOUT.STRIPE_EXPRESS_PAY}
          className={className}
          width="100%"
        >
          {/*
        The Payment Request Button Element dynamically displays wallet options during checkout,
        giving you a SINGLE integration for Apple Pay, Google Pay, and Link.
        ref: https://stripe.com/docs/stripe-js/elements/payment-request-button?client=html
        */}
          <PaymentRequestButtonElement
            options={{ ...options, paymentRequest }}
            className="payment-request-btn"
            onClick={trackClick}
          />

          {/*
        The Apple Pay Merchandising Guidelines require that you display the Apple Pay mark
        in close proximity to the button that the user taps to initiate an Apple Pay payment.
        ref: https://stripe.com/docs/stripe-js/elements/payment-request-button?client=html
        */}
          {withApplePayWidget && (
            <ApplePayWidget amount={(cartTotal / 100)?.toString() ?? '0'} />
          )}
        </Box>
      );
    }

    return null;
  } else {
    return (
      <Button
        variant="primary"
        size="wide"
        colorScheme="greenCS"
        isLoading
        height={buttonHeight}
      >
        Loading
      </Button>
    );
  }
};

export default StripePayment;
